import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["displayMonth", "displayYear", "children", "timezone", "eventDisplay", "hideWeekends"];
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { trackCalendarInteraction } from 'ui-addon-calendars/lib/usageTracker';
import CALENDAR_VIEW_TYPES from 'ui-addon-calendars/constants/CalendarViewTypes';
import { getDisplayMonthRows } from '../../lib/time';
import { useCalendarContext } from '../CalendarContextProvider';
import MonthCalendarView from './MonthCalendarView';
import { useTheme } from '../../lib/styles';
// @ts-expect-error not yet typed
import { getEventMapForMonth } from '../../lib/eventMap';
import { jsx as _jsx } from "react/jsx-runtime";
function MonthCalendarWrapper(_ref) {
  let {
      displayMonth = 0,
      displayYear,
      children,
      timezone,
      eventDisplay,
      hideWeekends
    } = _ref,
    restProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    closeCurrentPopover
  } = useCalendarContext();
  const theme = useTheme();
  const [openPopover, setOpenPopover] = useState(null);
  const [maxEventDisplayCount, setMaxEventDisplayCount] = useState(undefined);
  const calRefRef = useRef(null);
  const previousDisplayCountRef = useRef(undefined);
  const resizeObserverRef = useRef(null);
  const events = useMemo(() => getEventMapForMonth({
    children,
    displayYear,
    displayMonth,
    maxEventDisplayCount,
    timezone,
    eventDisplay,
    hideWeekends
  }), [children, displayYear, displayMonth, maxEventDisplayCount, timezone, eventDisplay, hideWeekends]);
  const handleResize = useCallback(entry => {
    const monthRows = getDisplayMonthRows(displayYear, displayMonth);
    const newMaxEventDisplayCount = theme.uiAddonCalendars.util.maxMonthEventDisplayCount(entry.contentRect.height, monthRows);
    if (newMaxEventDisplayCount !== previousDisplayCountRef.current) {
      setMaxEventDisplayCount(newMaxEventDisplayCount);
    }
    previousDisplayCountRef.current = newMaxEventDisplayCount;
  }, [displayYear, displayMonth, theme]);
  const registerCalRef = useCallback(calRef => {
    if (calRef) {
      calRefRef.current = calRef;
      if (resizeObserverRef.current) {
        resizeObserverRef.current.observe(calRef);
      }
    }
  }, []);
  const handleOverflowClose = useCallback(() => {
    setOpenPopover(null);
  }, []);
  const handleOverflowClick = useCallback(popoverKey => {
    trackCalendarInteraction({
      action: 'click view more',
      viewType: CALENDAR_VIEW_TYPES.MONTH,
      total: popoverKey.overflowCount
    });
    setOpenPopover(popoverKey);
    closeCurrentPopover();
  }, [closeCurrentPopover]);
  useEffect(() => {
    resizeObserverRef.current = new ResizeObserver(entries => {
      for (const entry of entries) {
        handleResize(entry);
      }
    });
    if (calRefRef.current) {
      resizeObserverRef.current.observe(calRefRef.current);
    }
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [handleResize]);
  return /*#__PURE__*/_jsx(MonthCalendarView, Object.assign({
    displayMonth: displayMonth,
    displayYear: displayYear,
    timezone: timezone,
    eventDisplay: eventDisplay,
    hideWeekends: hideWeekends
  }, restProps, {
    events: events,
    maxEventDisplayCount: maxEventDisplayCount,
    onOverflowButtonClick: handleOverflowClick,
    openPopover: openPopover,
    onPopoverClose: handleOverflowClose,
    calendarRef: registerCalRef
  }));
}
export default MonthCalendarWrapper;